import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Upload, Input, Select, Space, message,
  Modal, Tag, Popconfirm, Form, Tooltip, Progress
} from 'antd';
import { UploadOutlined, ReloadOutlined, DownloadOutlined, PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LockOutlined } from '@ant-design/icons';
import { importUsers, getUserList, addUser, resetPassword, exportData, updateUser, changeUserPassword, getDepartments } from '../../api/admin';

const { Search } = Input;
const { Option } = Select;

const UserManage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  // 加载部门列表
  const loadDepartments = async () => {
    try {
      const res = await getDepartments();
      if (res.code === 200) {
        setDepartments(res.data);
      }
    } catch (error) {
      console.error('加载部门列表失败:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadDepartments();
  }, [pagination.current, searchKeyword, departmentFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUserList({
        page: pagination.current,
        size: pagination.pageSize,
        keyword: searchKeyword,
        department: departmentFilter
      });
      
      if (response.code === 200) {
        setUsers(response.data.users);
        setPagination({ ...pagination, total: response.data.total });
      }
    } catch (error) {
      message.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      message.loading({ content: '导入中...', key: 'import' });
      const response = await importUsers(formData);
      
      if (response.code === 200) {
        message.success({ 
          content: `导入成功!共导入${response.data.imported_count}条记录`, 
          key: 'import' 
        });
        
        if (response.data.error_rows.length > 0) {
          Modal.warning({
            title: '部分数据导入失败',
            content: (
              <div>
                <p>失败行数: {response.data.error_rows.length}</p>
                <ul>
                  {response.data.error_rows.slice(0, 5).map((row, index) => (
                    <li key={index}>
                      第{row.row}行: {row.error}
                    </li>
                  ))}
                </ul>
              </div>
            )
          });
        }
        
        loadUsers();
      }
    } catch (error) {
      message.error({ content: error.message || '导入失败', key: 'import' });
    }
    
    return false;
  };

  const handleAddUser = async () => {
    try {
      const values = await form.validateFields();
      const response = await addUser(values);
      
      if (response.code === 200) {
        message.success('添加成功');
        setAddModalVisible(false);
        form.resetFields();
        loadUsers();
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '添加失败');
    }
  };

  const handleResetPassword = async (id) => {
    try {
      const response = await resetPassword(id);
      if (response.code === 200) {
        message.success('密码已重置为: 123456');
      }
    } catch (error) {
      message.error(error.message || '重置失败');
    }
  };

  const handleEditUser = (record) => {
    setEditingUser(record);
    editForm.setFieldsValue({
      name: record.name,
      employee_id: record.employee_id,
      department_id: record.department_id,
      position: record.position,
      phone: record.phone
    });
    setEditModalVisible(true);
  };

  const handleChangePwd = (record) => {
    setEditingUser(record);
    pwdForm.resetFields();
    setPwdModalVisible(true);
  };

  const handleSubmitPwd = async () => {
    try {
      const values = await pwdForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        message.error('两次密码不一致');
        return;
      }
      const response = await changeUserPassword(editingUser.id, { new_password: values.new_password });
      if (response.code === 200) {
        message.success('密码修改成功');
        setPwdModalVisible(false);
        setEditingUser(null);
        pwdForm.resetFields();
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '修改失败');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const values = await editForm.validateFields();
      const response = await updateUser(editingUser.id, values);
      
      if (response.code === 200) {
        message.success('用户资料已更新');
        setEditModalVisible(false);
        setEditingUser(null);
        editForm.resetFields();
        loadUsers();
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '更新失败');
    }
  };

  const handleExport = async () => {
    try {
      message.loading({ content: '导出中...', key: 'export' });
      const response = await exportData('progress');
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `用户进度_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success({ content: '导出成功', key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'export' });
    }
  };
   // 导出评分详情
  const handleExportScores = async () => {
    try {
      message.loading({ content: '导出中...', key: 'exportScores' });
      const response = await exportData('scores');
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `评分详情_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success({ content: '导出成功', key: 'exportScores' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'exportScores' });
    }
  };
  // 渲染步骤完成状态
  const renderStepStatus = (progress, stepKey) => {
    const isCompleted = progress?.[stepKey];
    return isCompleted ? (
      <Tooltip title="已完成">
        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
      </Tooltip>
    ) : (
      <Tooltip title="未完成">
        <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />
      </Tooltip>
    );
  };

  const columns = [
    {
      title: '工号',
      dataIndex: 'employee_id',
      key: 'employee_id',
      width: 100,
      fixed: 'left'
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 80,
      fixed: 'left'
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      width: 100
    },
    {
      title: '岗位',
      dataIndex: 'position',
      key: 'position',
      width: 100
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120
    },
    {
      title: '跟读',
      dataIndex: 'progress',
      key: 'step1',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step1_completed')
    },
    {
      title: '播客',
      dataIndex: 'progress',
      key: 'step2',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step2_completed')
    },
    {
      title: '填空',
      dataIndex: 'progress',
      key: 'step3',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step3_completed')
    },
    {
      title: '评分',
      dataIndex: 'progress',
      key: 'step4',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step4_completed')
    },
    {
      title: '找茬',
      dataIndex: 'progress',
      key: 'step5',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step5_completed')
    },
    {
      title: '承诺',
      dataIndex: 'progress',
      key: 'step6',
      width: 60,
      align: 'center',
      render: (progress) => renderStepStatus(progress, 'step6_completed')
    },
    {
      title: '进度',
      key: 'progress_rate',
      width: 100,
      render: (_, record) => {
        const progress = record.progress || {};
        const steps = ['step1_completed', 'step2_completed', 'step3_completed', 'step4_completed', 'step5_completed', 'step6_completed'];
        const completed = steps.filter(s => progress[s]).length;
        const percent = Math.round((completed / 6) * 100);
        return (
          <Progress 
            percent={percent} 
            size="small" 
            format={() => `${completed}/6`}
            strokeColor={percent === 100 ? '#52c41a' : '#1890ff'}
          />
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'progress',
      key: 'status',
      width: 80,
      render: (progress) => {
        if (progress?.all_completed) {
          return <Tag color="success">已完成</Tag>;
        }
        return <Tag color="processing">学习中</Tag>;
      }
    },
    {
      title: '完成时间',
      dataIndex: 'progress',
      key: 'completed_at',
      width: 150,
      render: (progress) => {
        if (progress?.completed_at) {
          return new Date(progress.completed_at).toLocaleString('zh-CN');
        }
        return '-';
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="修改">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
          <Tooltip title="改密码">
            <Button
              type="link"
              size="small"
              icon={<LockOutlined />}
              onClick={() => handleChangePwd(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定重置密码?"
            description="密码将被重置为123456"
            onConfirm={() => handleResetPassword(record.id)}
          >
            <Tooltip title="重置密码">
              <Button type="link" size="small" danger icon={<ReloadOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <h2 style={{ marginBottom: 16 }}>用户管理</h2>
      
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
          >
            添加用户
          </Button>
          
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleImport}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Excel导入</Button>
          </Upload>
          
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleExport}
          >
            导出报表
          </Button>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleExportScores}
          >
            导出评分详情
          </Button>
        </Space>
        
        <Space>
          <Search
            placeholder="搜索工号/姓名"
            onSearch={setSearchKeyword}
            style={{ width: 200 }}
            allowClear
          />
          
          <Select
            placeholder="选择部门"
            style={{ width: 150 }}
            onChange={setDepartmentFilter}
            allowClear
          >
            {departments.map(dept => (
              <Option key={dept.id} value={dept.name}>{dept.name}</Option>
            ))}
          </Select>
          
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadUsers}
          >
            刷新
          </Button>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`
        }}
        onChange={(newPagination) => {
          setPagination(newPagination);
        }}
      />

      {/* 添加用户弹窗 */}
      <Modal
        title="添加用户"
        open={addModalVisible}
        onOk={handleAddUser}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="employee_id"
            label="工号"
            rules={[{ required: true, message: '请输入工号' }]}
          >
            <Input placeholder="请输入工号" />
          </Form.Item>

          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <Select placeholder="请选择部门">
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="position"
            label="岗位"
          >
            <Input placeholder="请输入岗位（可选）" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
          >
            <Input placeholder="请输入手机号（可选）" />
          </Form.Item>

          <Form.Item
            name="password"
            label="初始密码"
            extra="默认密码为 123456"
          >
            <Input.Password placeholder="留空则默认为123456" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改用户资料弹窗 */}
      <Modal
        title="修改用户资料"
        open={editModalVisible}
        onOk={handleUpdateUser}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingUser(null);
          editForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="employee_id"
            label="工号"
            rules={[{ required: true, message: '请输入工号' }]}
          >
            <Input placeholder="请输入工号" />
          </Form.Item>

          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <Select placeholder="请选择部门">
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>{dept.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="position"
            label="岗位"
          >
            <Input placeholder="请输入岗位" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>
        </Form>
      </Modal>
      {/* 修改密码弹窗 */}
      <Modal
        title={`修改密码 - ${editingUser?.name || ''}`}
        open={pwdModalVisible}
        onOk={handleSubmitPwd}
        onCancel={() => {
          setPwdModalVisible(false);
          setEditingUser(null);
          pwdForm.resetFields();
        }}
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码不能少于6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码（不少于6位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            rules={[{ required: true, message: '请再次输入新密码' }]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default UserManage;
